import { useState, useEffect } from 'react';
import { Sparkles, Plus, X, Loader2, AlertCircle, Flame, Star, Eye } from 'lucide-react';

interface Category {
  id: string;
  slug: string;
  display_name: string;
  description: string | null;
  sort_order: number;
  keywords: string[];
}

interface Interest {
  id: string;
  category_id: string;
  interest_level: 'high' | 'interested' | 'exploring';
  display_name: string;
  slug: string;
  was_merged: boolean;
  added_via: string;
  created_at: string;
}

const LEVEL_META: Record<Interest['interest_level'], { label: string; icon: typeof Flame; color: string; boost: string }> = {
  high:        { label: 'Høy prioritet', icon: Flame, color: 'text-orange-600 bg-orange-50 border-orange-300',   boost: '+10' },
  interested:  { label: 'Interessert',   icon: Star,  color: 'text-blue-600 bg-blue-50 border-blue-300',         boost: '+6' },
  exploring:   { label: 'Utforsker',     icon: Eye,   color: 'text-slate-600 bg-slate-100 border-slate-300',     boost: '+3' }
};

export function InterestProfileSection() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [interests, setInterests] = useState<Interest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showPicker, setShowPicker] = useState(false);
  const [saving, setSaving] = useState<string | null>(null);

  useEffect(() => {
    loadAll();
  }, []);

  async function loadAll() {
    setLoading(true);
    setError(null);
    try {
      const [catsRes, interestsRes] = await Promise.all([
        fetch('/api/candidate/interests/categories', { credentials: 'include' }),
        fetch('/api/candidate/interests', { credentials: 'include' })
      ]);
      if (!catsRes.ok) throw new Error(`Kategorier: ${catsRes.status}`);
      if (!interestsRes.ok) throw new Error(`Interesser: ${interestsRes.status}`);
      const catsData = await catsRes.json();
      const intsData = await interestsRes.json();
      setCategories(catsData.categories || []);
      setInterests(intsData.interests || []);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function addInterest(category_id: string, level: Interest['interest_level'] = 'interested') {
    setSaving(category_id);
    try {
      const res = await fetch('/api/candidate/interests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ category_id, interest_level: level, added_via: 'manual' })
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `HTTP ${res.status}`);
      }
      await loadAll();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(null);
    }
  }

  async function changeLevel(interestId: string, newLevel: Interest['interest_level']) {
    setSaving(interestId);
    try {
      const res = await fetch(`/api/candidate/interests/${interestId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ interest_level: newLevel })
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await loadAll();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(null);
    }
  }

  async function removeInterest(interestId: string) {
    setSaving(interestId);
    try {
      const res = await fetch(`/api/candidate/interests/${interestId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (!res.ok && res.status !== 204) throw new Error(`HTTP ${res.status}`);
      await loadAll();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(null);
    }
  }

  const chosenCategoryIds = new Set(interests.map(i => i.category_id));
  const availableCategories = categories.filter(c => !chosenCategoryIds.has(c.id));

  if (loading) {
    return (
      <section className="bg-white rounded-xl border border-slate-200 p-6">
        <Loader2 className="h-6 w-6 animate-spin text-kf-blue-600 mx-auto" />
      </section>
    );
  }

  return (
    <section className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-purple-600" />
            Interesse-områder
          </h2>
          <p className="text-sm text-slate-600 mt-1">
            Velg tema du er interessert i — vi bruker det til å foreslå kurs som passer.
            Endres når som helst.
          </p>
        </div>
      </header>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800 flex gap-2">
          <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {/* Registrerte interesser */}
      {interests.length === 0 ? (
        <div className="text-center py-6 text-slate-500 text-sm border border-dashed border-slate-300 rounded-lg">
          Ingen interesser registrert ennå
        </div>
      ) : (
        <ul className="space-y-2">
          {interests.map(interest => {
            const meta = LEVEL_META[interest.interest_level];
            const Icon = meta.icon;
            const isBusy = saving === interest.id;
            return (
              <li key={interest.id} className="flex items-center gap-3 p-3 bg-slate-50 border border-slate-200 rounded-lg">
                <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded border ${meta.color}`}>
                  <Icon className="h-3 w-3" />
                  {meta.label}
                </span>
                <span className="flex-1 text-sm font-medium text-slate-900">{interest.display_name}</span>
                {interest.was_merged && (
                  <span className="text-xs text-slate-500 italic">omdøpt</span>
                )}
                {/* 3-nivå bytter */}
                <div className="flex gap-1">
                  {(['high','interested','exploring'] as const).map(lvl => (
                    <button
                      key={lvl}
                      onClick={() => interest.interest_level !== lvl && changeLevel(interest.id, lvl)}
                      disabled={isBusy || interest.interest_level === lvl}
                      title={LEVEL_META[lvl].label}
                      className={`p-1.5 rounded transition-colors ${
                        interest.interest_level === lvl
                          ? LEVEL_META[lvl].color
                          : 'text-slate-400 hover:bg-slate-200'
                      }`}
                    >
                      {(() => {
                        const I = LEVEL_META[lvl].icon;
                        return <I className="h-4 w-4" />;
                      })()}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => removeInterest(interest.id)}
                  disabled={isBusy}
                  className="p-1.5 text-slate-400 hover:text-red-600 rounded"
                  title="Fjern interesse"
                >
                  {isBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {/* Legg-til-panel */}
      <div>
        {!showPicker ? (
          <button
            onClick={() => setShowPicker(true)}
            className="inline-flex items-center gap-2 text-sm px-4 py-2 bg-kf-blue-600 text-white rounded-md hover:bg-kf-blue-700"
          >
            <Plus className="h-4 w-4" /> Legg til interesse
          </button>
        ) : (
          <div className="border border-slate-200 rounded-lg p-4 bg-slate-50">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-slate-700">Velg kategorier ({availableCategories.length} tilgjengelig)</span>
              <button onClick={() => setShowPicker(false)} className="text-slate-500 hover:text-slate-700 text-sm">Lukk</button>
            </div>
            {availableCategories.length === 0 ? (
              <p className="text-sm text-slate-500 italic">Du har lagt til alle tilgjengelige kategorier</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {availableCategories.map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => addInterest(cat.id, 'interested')}
                    disabled={saving === cat.id}
                    title={cat.description || `Legg til ${cat.display_name}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white border border-slate-300 rounded-full hover:border-kf-blue-500 hover:bg-kf-blue-50 disabled:opacity-50"
                  >
                    {saving === cat.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                    {cat.display_name}
                  </button>
                ))}
              </div>
            )}
            <p className="text-xs text-slate-500 mt-3">
              Nye interesser legges til som «Interessert» (+6). Endre til «Høy prioritet» (+10) eller «Utforsker» (+3) med ikonene til høyre for hver interesse.
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
